import { useCallback, useMemo, useState } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToSqafInsightCards } from "@/utils/mistralInsightMappers";
import { buildSqafSectionMarkdown } from "@/utils/sqaafMistralInsights";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Plus,
  RefreshCw,
  Search,
  Target,
  TrendingUp,
} from "lucide-react";
import { fmt2 } from "@/lib/sqaafLabels";
import { toTitleCase } from "@/utils/toTitleCase";
import {
  buildFocusList,
  buildGapChartRows,
  buildSectionHeroCopy,
  buildSectionHeroMetrics,
  buildSectionInsightCards,
  buildSectionRankRows,
  buildSectionTableRows,
  filterSectionsByCoverage,
  filterSectionsByDomain,
  mergeSectionRows,
} from "@/utils/sqaafSectionDerived";

export default function SqafDomainSection({
  overview,
  sectionAnalysis,
  showSkeleton,
  refreshing,
  onRefresh,
  onExport,
  onDownloadCsv,
}) {
  const [domainFilter, setDomainFilter] = useState("All Domains");
  const [coverageFilter, setCoverageFilter] = useState("All Sections");
  const [search, setSearch] = useState("");

  const districtMean = sectionAnalysis?.district_mean ?? overview?.district_mean ?? 0;

  const allSections = useMemo(() => mergeSectionRows(sectionAnalysis), [sectionAnalysis]);

  const filteredSections = useMemo(() => {
    let rows = filterSectionsByDomain(allSections, domainFilter);
    rows = filterSectionsByCoverage(rows, coverageFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (s) =>
          String(s.section).toLowerCase().includes(q) ||
          String(s.section_label || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [allSections, domainFilter, coverageFilter, search]);

  const hasData = Boolean(allSections.length);
  const buildLocalMarkdown = useCallback(
    () => buildSqafSectionMarkdown({ overview, sectionAnalysis }),
    [overview, sectionAnalysis]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ n: allSections.length, m: districtMean }),
    [allSections.length, districtMean]
  );
  const {
    loading: aiLoading,
    pickFromBucket,
    pickHeadline,
    pickPrimaryAction,
    pickMapped,
    isMistral,
  } = useMistralNarrativeOverlay({
    kind: "sqaaf-sections",
    title: "SQAAF Domain & Section Analysis",
    buildLocalMarkdown,
    context: { overview, sectionAnalysis },
    enabled: hasData,
    scopeKey,
  });

  const localHero = useMemo(
    () => buildSectionHeroCopy(overview, allSections, districtMean),
    [overview, allSections, districtMean]
  );
  const hero = useMemo(() => {
    if (aiLoading) return localHero;
    return {
      ...localHero,
      headline: pickHeadline(localHero.headline),
      body: pickFromBucket(localHero.body, "insights", 0) || localHero.body,
      actionNote: pickFromBucket(localHero.actionNote, "root", 0) || pickPrimaryAction(localHero.actionNote),
      chips: isMistral
        ? [{ label: "Mistral AI Assisted", className: "purple" }, ...localHero.chips]
        : localHero.chips,
    };
  }, [aiLoading, localHero, pickHeadline, pickFromBucket, pickPrimaryAction, isMistral]);

  const heroMetrics = buildSectionHeroMetrics(overview, allSections, districtMean);
  const rankRows = buildSectionRankRows(filteredSections, districtMean);
  const gapRows = buildGapChartRows(filteredSections, districtMean);
  const lowestFocus = buildFocusList(allSections, "low", 4);
  const strongestFocus = buildFocusList(allSections, "high", 4);
  const localInsightCards = useMemo(
    () => buildSectionInsightCards(allSections, districtMean),
    [allSections, districtMean]
  );
  const insightCards = useMemo(
    () => (aiLoading ? localInsightCards : pickMapped(localInsightCards, "insights", bulletsToSqafInsightCards)),
    [aiLoading, pickMapped, localInsightCards]
  );
  const tableRows = buildSectionTableRows(
    filteredSections.sort((a, b) => String(a.section).localeCompare(String(b.section), undefined, { numeric: true })),
    districtMean
  );

  if (showSkeleton && !overview) {
    return <div className="sq-exec-skeleton" data-testid="sqaf-section-skeleton" />;
  }

  return (
    <>
      <section className="page-header" id="domains">
        <div>
          <div className="eyebrow">
            <Target className="svg-icon" strokeWidth={2} />
            Section intelligence · Pune District · 2025–26
          </div>
          <h1>Domain / Section</h1>
          <p>
            Scan section performance, identify where scores fall below the district baseline, and move directly from
            diagnosis to improvement action.
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="ghost-action" onClick={onExport}>
            <Download className="svg-icon" strokeWidth={2} />
            Download CSV
          </button>
          <button type="button" className="primary-action" onClick={onRefresh} disabled={refreshing}>
            <RefreshCw className={`svg-icon ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>
      </section>

      <section className="scope-card" aria-label="Domain section filters">
        <div className="filter-row">
          <span className="filter-chip">
            Scope <b>Pune District</b>
          </span>
          <span className="filter-chip">
            View <b>Section Summary</b>
          </span>
          <label className="filter-chip">
            Domain{" "}
            <b>
              <select
                className="sq-inline-select"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                aria-label="Filter by domain"
              >
                {["All Domains", "Domain 1", "Domain 2"].map((d) => (
                  <option key={d} value={d}>
                    {d === "All Domains" ? "All Domains" : d}
                  </option>
                ))}
              </select>
            </b>
          </label>
          <label className="filter-chip">
            Coverage{" "}
            <b>
              <select
                className="sq-inline-select"
                value={coverageFilter}
                onChange={(e) => setCoverageFilter(e.target.value)}
                aria-label="Filter by coverage"
              >
                <option value="All Sections">All Sections</option>
                <option value="Low coverage">Low coverage</option>
              </select>
            </b>
          </label>
        </div>
        <div className="scope-note">
          Active: {allSections.length} sections · {overview?.n_questions || 128} questions · District mean{" "}
          {fmt2(districtMean)}
        </div>
      </section>

      <section className="hero-summary">
        <div className="hero-layout">
          <div>
            <div className="focus-shell">
              <div className="ring" style={hero.ringStyle}>
                <div>
                  <strong>{hero.meanScore}</strong>
                  <span>Mean Score</span>
                </div>
              </div>
              <div className="score-copy">
                <h2 className="dashboard-kpi-hero-title">{toTitleCase(hero.headline)}</h2>
                <p>{hero.body}</p>
                <div className="status-row">
                  {hero.chips.map((chip) => (
                    <span key={chip.label} className={`status-chip ${chip.className}`.trim()}>
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="summary-note">
              <div className="icon-box purple">
                <Plus className="svg-icon" strokeWidth={2} />
              </div>
              <div>
                <b>Executive signal</b>
                <span>{hero.actionNote}</span>
              </div>
            </div>
          </div>

          <div className="metric-grid">
            {heroMetrics.map((metric) => (
              <article key={metric.label} className="metric-card">
                <div className="metric-head">
                  <div>
                    <small>{metric.label}</small>
                    <strong>{metric.value}</strong>
                    <span>{metric.sub}</span>
                  </div>
                  <span className={`mini-badge ${metric.badge}`.trim()}>
                    {metric.icon === "up" && <TrendingUp className="svg-icon" strokeWidth={2} />}
                    {metric.icon === "down" && <TrendingUp className="svg-icon sq-icon-down" strokeWidth={2} />}
                    {metric.icon === "dot" && "•"}
                    {metric.icon === "bang" && "!"}
                    {metric.icon === "check" && <CheckCircle2 className="svg-icon" strokeWidth={2} />}
                    {metric.icon === "warn" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="section-title">
        <div>
          <h2>Section Ranking and Focus View</h2>
          <p>
            The left view shows ranked section means from low to high. The right panel turns the data into quick action
            cues for district reviewers.
          </p>
        </div>
        <a className="section-action" href="#summary-table">
          Jump to section table →
        </a>
      </div>

      <section className="analytics-grid sq-section-analytics">
        <article className="panel">
          <div className="panel-head">
            <div>
              <h3>Section Mean Scores</h3>
              <p>Ordered from lowest to highest to make weak sections immediately visible. Gray marker = district mean.</p>
            </div>
            <span className="pill">Ranked view</span>
          </div>
          <div className="rank-list" aria-label="Section mean scores ranked low to high">
            {rankRows.map((row) => (
              <div key={row.section} className="rank-row sq-section-rank-row">
                <div className="rank-label">
                  <b>{row.section}</b>
                  <span>{row.label}</span>
                </div>
                <div className="rank-track">
                  <div className="rank-fill" style={{ width: `${row.width}%` }} />
                  <div className="district-marker" style={{ left: `${row.markerLeft}%` }} />
                </div>
                <div className="rank-value">{fmt2(row.mean)}</div>
              </div>
            ))}
          </div>
        </article>

        <aside className="side-stack">
          <div className="panel mini-panel">
            <h4>Lowest scoring sections</h4>
            <div className="focus-list">
              {lowestFocus.map((item) => (
                <div key={item.section} className="focus-item">
                  <span>{item.section}</span>
                  <b>{item.mean}</b>
                </div>
              ))}
            </div>
            <p>These sections should be reviewed first for intervention planning and targeted mentoring support.</p>
          </div>
          <div className="panel mini-panel">
            <h4>Strongest sections</h4>
            <div className="focus-list">
              {strongestFocus.map((item) => (
                <div key={item.section} className="focus-item">
                  <span>{item.section}</span>
                  <b>{item.mean}</b>
                </div>
              ))}
            </div>
            <p>Use these sections as benchmarks to replicate good practices across weaker clusters and blocks.</p>
          </div>
          <div className="panel mini-panel">
            <h4>Improvement guidance</h4>
            <p>
              Prioritize sections with low score and low coverage together. This usually indicates both implementation
              weakness and evidence gaps.
            </p>
            <div className="legend-row">
              <span className="legend-pill">
                <i className="dot purple" /> Section mean
              </span>
              <span className="legend-pill">
                <i className="dot gray" /> District benchmark
              </span>
              <span className="legend-pill">
                <i className="dot red" /> Critical focus
              </span>
              <span className="legend-pill">
                <i className="dot green" /> Healthy section
              </span>
            </div>
          </div>
        </aside>
      </section>

      <div className="section-title">
        <div>
          <h2>Gap vs District Mean</h2>
          <p>
            Sections below the center line are underperforming the district baseline; sections above it are performing
            better than the district average.
          </p>
        </div>
        <a className="section-action" href="#insights">
          View insights →
        </a>
      </div>

      <section className="panel gap-panel">
        <div className="panel-head">
          <div>
            <h3>Section Gap Analysis</h3>
            <p>Difference between section mean and district mean ({fmt2(districtMean)}).</p>
          </div>
          <span className="pill">Negative vs positive</span>
        </div>
        <div className="chart-surface">
          <div className="gap-chart" aria-label="Section gap versus district mean">
            <div className="gap-center" />
            {gapRows.map((row) => (
              <div key={row.section} className="gap-col" title={row.title}>
                <div className={`gap-bar ${row.direction}`} style={{ height: `${row.height}px` }} />
                <span>{row.section}</span>
              </div>
            ))}
          </div>
          <div className="gap-scale">
            <span>Below district mean</span>
            <span>At district mean</span>
            <span>Above district mean</span>
          </div>
        </div>
      </section>

      <section className="insight-grid" id="insights">
        {insightCards.map((card) => (
          <article key={card.title} className="insight-card">
            <div className={`icon-box ${card.tone}`}>
              {card.tone === "red" && <AlertTriangle className="svg-icon" strokeWidth={2} />}
              {card.tone === "green" && <TrendingUp className="svg-icon" strokeWidth={2} />}
              {card.tone === "orange" && <Clock className="svg-icon" strokeWidth={2} />}
            </div>
            <h3>{toTitleCase(card.title)}</h3>
            <p>{card.body}</p>
          </article>
        ))}
      </section>

      <section className="panel table-card" id="summary-table">
        <div className="table-head">
          <div>
            <h3>Section Summary Table</h3>
            <p>Compact diagnostic table for sorting, exporting and downstream action mapping.</p>
          </div>
          <div className="table-tools">
            <label className="search-box">
              <Search className="svg-icon" strokeWidth={2} />
              <input
                placeholder="Search section..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search sections"
              />
            </label>
            <button type="button" className="ghost-action" onClick={onDownloadCsv}>
              Export filtered view
            </button>
          </div>
        </div>
        <div className="table-wrap">
          <table aria-label="Section summary table">
            <thead>
              <tr>
                <th>Section</th>
                <th>Section Name</th>
                <th>Questions</th>
                <th>Coverage %</th>
                <th>Mean</th>
                <th>Median</th>
                <th>Gap vs District</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.section}>
                  <td>
                    <b>{row.section}</b>
                  </td>
                  <td>{row.section_label}</td>
                  <td>{row.question_count}</td>
                  <td>{row.coverage_pct}</td>
                  <td>{row.mean}</td>
                  <td>{row.median}</td>
                  <td className={row.gapClass}>{row.gap}</td>
                  <td>
                    <span className={`tag ${row.priorityTag}`}>{row.priority}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
